import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { Ikr998Data } from "../interfaces/ert-kr998/ert-kr998Data";
import { Kr998Service } from "../services/kr998/kr998.service";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { TrainingType } from "../services/administration/trainingtypes/trainingtypes";
import { IPicklistValuesData } from "../interfaces/administration/picklistValuesData";
import { PicklistService } from "../services/administration/picklist/picklist.service";
import { forkJoin } from "rxjs";
import {
  FilterCriteria,
  FilterService,
} from "../services/common/filter.service";
import { LoggedUserService } from "../services/common/logged-user.service";
import { ILoggedUserData } from "../interfaces/common/loggedUserData";

@Component({
  selector: "ert-kr998-component",
  templateUrl: "./ert-kr998.component.html",
  styles: [
    `
      .button-container {
        justify-content: center !important;
        display: flex;
        margin-bottom: 10px;
      }
    `,
  ],
})
export class ERTKr998Component implements OnInit {
  title = "KR-998";
  totalItems: number;
  sortedBy: string;
  pageSize = 10;
  searchForm: FormGroup;
  isKr998Loading = false;

  // Filter properties
  activeFilters: FilterCriteria[] = [];
  showFilterPanel = false;

  // Storage keys - use unique keys for this component
  readonly kr998_filters_storage_key = "ert-kr998-filters";
  readonly kr998_saved_filters_storage_key = "ert-kr998-saved-filters";

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["id", "ID"],
    ["startDateTime", "Start Date"],
    ["crew", "Crew"],
    ["company", "Company"],
    ["location", "Location"],
    ["trainingTypeName", "Training Type"],
    ["actions", "Actions"],
  ]);
  dataSource = new MatTableDataSource<Ikr998Data>();
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  crewFieldId: number = 0;

  currentUser: ILoggedUserData;

  constructor(
    private router: Router,
    private k998Service: Kr998Service,
    private pickListsService: PicklistService,
    private formBuilder: FormBuilder,
    private filterService: FilterService,
    private loggedUserService: LoggedUserService
  ) {}

  ngOnInit(): void {
    this.isKr998Loading = true;
    this.initSearchForm();
    forkJoin({
      crew: this.pickListsService.getFieldIdByName("Crew"),
    }).subscribe((results) => {
      this.crewFieldId = results.crew || 0;
      this.isKr998Loading = false;

      this.loggedUserService.getLoggedUserData().subscribe((user) => {
        this.currentUser = user;
        const facilityId = Number(this.currentUser.facilityId) || 1;
        this.loadData(facilityId);
      });
    });

    // Initialize filter service
    this.filterService.loadActiveFiltersFromStorage(
      this.kr998_filters_storage_key
    );
    this.filterService.loadSavedFiltersFromStorage(
      this.kr998_saved_filters_storage_key
    );
  }

  initSearchForm(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [""],
    });

    this.searchForm.get("searchTerm")?.valueChanges.subscribe((value) => {
      this.applyFilter(value);
    });
  }

  applyFilter(filterValue: string): void {
    this.dataSource.filterPredicate = (
      data: Ikr998Data,
      filterString: string
    ) => {
      const parts = filterString.split("||");
      const searchTerm = parts[0].toLowerCase();
      let activeFilters: FilterCriteria[] = [];

      try {
        if (parts[1]) {
          activeFilters = JSON.parse(parts[1]);
        }
      } catch (error) {
        console.error("Error parsing active filters:", error);
      }

      // Basic search
      const searchMatch = searchTerm
        ? String(data.id || "")
            .toLowerCase()
            .includes(searchTerm) ||
          (data.company || "").toLowerCase().includes(searchTerm) ||
          (data.location || "").toLowerCase().includes(searchTerm) ||
          (data.crew || "").toLowerCase().includes(searchTerm) ||
          (data.trainingTypeName || "").toLowerCase().includes(searchTerm)
        : true; // If no search term, consider it a match

      // Advanced filters match
      let advancedFiltersMatch = true;
      if (activeFilters.length > 0) {
        advancedFiltersMatch = activeFilters.every((filter) => {
          const value = this.getPropertyValue(data, filter.column);

          if (value === null || value === undefined) {
            return false;
          }

          switch (filter.operator) {
            case "contains":
              return String(value)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case "equals":
              return (
                String(value).toLowerCase() ===
                String(filter.value).toLowerCase()
              );
            case "startsWith":
              return String(value)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case "endsWith":
              return String(value)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case "greaterThan":
              return Number(value) > Number(filter.value);
            case "lessThan":
              return Number(value) < Number(filter.value);
            default:
              return true;
          }
        });
      }

      return searchMatch && advancedFiltersMatch;
    };

    // Combine search term and active filters
    const combinedFilter =
      filterValue.trim().toLowerCase() +
      "||" +
      JSON.stringify(this.activeFilters);
    this.dataSource.filter = combinedFilter;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }

    this.totalItems = this.dataSource.filteredData.length;
  }

  // Helper to get property value for filtering
  getPropertyValue(data: Ikr998Data, property: string): any {
    switch (property) {
      case "id":
        return data.id;
      case "startDateTime":
        return data.startDateTime;
      case "crew":
        return data.crew;
      case "company":
        return data.company;
      case "location":
        return data.location;
      case "trainingTypeName":
        return data.trainingTypeName;
      default:
        return "";
    }
  }

  clearSearch(): void {
    this.searchForm.get("searchTerm")?.setValue("");
  }

  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  onFiltersChanged(filters: FilterCriteria[]): void {
    this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
  }

  clearAllFilters() {
    this.filterService.clearAllFilters(this.kr998_filters_storage_key);
    this.searchForm.patchValue({ searchTerm: "" });
    this.applyFilter("");
  }

  getColumnDisplayName(columnKey: string): string {
    return this.displayedColumns.get(columnKey) || columnKey;
  }

  onSortChange(sortState: Sort) {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  loadData(facilityId: number) {
    this.isKr998Loading = true;
    this.k998Service.getAllKr998(facilityId).subscribe((data) => {
      setTimeout(() => {
        this.dataSource.data = data;
        this.totalItems = data.length;
        data.forEach((kr) => {
          kr.crew = this.getLastPostedCrew(kr.picklistValues);
          kr.trainingTypeName = this.getTrainingTypeName(kr.trainingType);
        });

        // Subscribe to active filters changes
        this.filterService
          .getActiveFilters(this.kr998_filters_storage_key)
          .subscribe((filters) => {
            this.activeFilters = filters;
            if (this.dataSource) {
              this.applyFilter(this.searchForm.get("searchTerm")?.value || "");
            }
          });

        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        // Sort by startDateTime in descending order (newest first)
        this.sort.sort({ id: "startDateTime", start: "desc" } as MatSortable);
        this.sort.sortChange.subscribe((sortState: Sort) => {
          this.onSortChange(sortState);
        });
        this.sortedBy = `Sorted by ${this.displayedColumns.get(
          "startDateTime"
        )}`;
      }, 10);
      this.isKr998Loading = false;
    });
  }

  getTrainingTypeName(trainingTypeId: number): string {
    return TrainingType[trainingTypeId];
  }

  deleteRecord(id: number) {
    if (window.confirm("Are you sure you want to delete this record?")) {
      this.k998Service
        .deleteKr998(id, Number(this.currentUser.facilityId) || 0)
        .subscribe(() => {
          const facilityId = Number(this.currentUser.facilityId) || 1;
          this.loadData(facilityId);
        });
    }
  }

  navigateToNewEntry() {
    this.router.navigate(["/ert-kr998/ert-new-kr998"]);
  }

  editKr(id: number) {
    this.router.navigate(["/ert-kr998/ert-edit-kr998", id]);
  }

  viewKr(id: number) {
    this.router.navigate(["/ert-kr998/ert-kr998-view", id]);
  }

  getLastPostedCrew(picklistValues: IPicklistValuesData[]): string {
    let name: string = this.k998Service.getValueForField(
      picklistValues,
      this.crewFieldId
    );
    return name;
  }
}
